import React from 'react';

import { ActionIcon, Tooltip, useMantineTheme } from '@mantine/core';
import { IconAlertTriangle, IconLink, IconLinkOff, IconUserOff } from '@tabler/icons-react';
import { useAuth } from 'context/auth-context';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { useNavigate } from 'react-router-dom';
import { AthleteList } from 'types';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Athlete-table GLM link-status indicator.
 *  - linked    → blue link; tooltip shows last-synced; click opens the GLM contact in a new tab
 *  - unlinked  → gray link-off; click opens the athlete detail (where the link card lives)
 *  - broken    → red warning; click opens the athlete detail to resolve
 *  - dependent → gray user-off; dependents can't be linked (matches the detail card)
 */
const GLMLinkStatusCell = ({ athlete }: { athlete: AthleteList }): JSX.Element => {
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const { session } = useAuth();
  const { trackAthleteGLMLinkIconClicked } = useStaffTracking();
  // Dependents can never be linked — surface that here as the card does, taking precedence
  // over the record-derived link state. Fall back to 'unlinked' when the state is absent.
  const state = athlete.connectedType === 'Dependent' ? 'dependent' : athlete.glmLinkState ?? 'unlinked';

  const goToDetail = (): void => navigate(`/users/athletes/${athlete.athleteId}/general-info`);

  const handleClick = (): void => {
    trackAthleteGLMLinkIconClicked({ athlete_id: athlete.athleteId, link_state: state });
    if (state === 'linked' && athlete.glmContactUrl) {
      window.open(athlete.glmContactUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    goToDetail();
  };

  let icon: JSX.Element;
  let tooltip: string;
  if (state === 'linked') {
    const synced = athlete.glmLastSyncedAt
      ? dayjs(athlete.glmLastSyncedAt)
          .tz(session?.gym?.timezone ?? 'UTC')
          .format('MMM D, YYYY h:mm A')
      : 'never';
    icon = <IconLink size={18} color={theme.colors.kiloBlue[6]} />;
    tooltip = `Linked to GLM · last synced ${synced}`;
  } else if (state === 'broken') {
    icon = <IconAlertTriangle size={18} color='red' />;
    tooltip = 'Last GLM link attempt failed — click to view details';
  } else if (state === 'dependent') {
    icon = <IconUserOff size={18} color='gray' />;
    tooltip = 'Dependent athletes cannot be linked to GLM';
  } else {
    icon = <IconLinkOff size={18} color='gray' />;
    tooltip = 'Not linked to GLM — click to link';
  }

  return (
    <Tooltip label={tooltip} withinPortal>
      <ActionIcon variant='subtle' onClick={handleClick} aria-label={`GLM ${state}`}>
        {icon}
      </ActionIcon>
    </Tooltip>
  );
};

export default GLMLinkStatusCell;
